import * as React from "react"
import { Form, Col } from "react-bootstrap"
import { concat, of, Subject, Subscription } from "rxjs"
import { filter, map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import { connect } from "react-redux"
import { EmailRegexp, PhoneRegexp } from "../../../config/Constants";
import MaskedInput from "react-maskedinput"
import { Addresses } from "../../../api/OrderRequests/OrderRequestsModel/Addresses";
import { CompanyNameCheck, EmailCheck } from "../../../api/Invitation/InformationGet"
import { GetRoles } from "../../../api/Roles/UserRolesGet"
import Role from "../../../api/Roles/Model/Role"
import { APIError } from "../../../api/ObservableFromFetch"
import { AppState } from "../../../AppState"
import { RoleIdForSelectedCompany } from "../../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import PortalAdminData from "../../../api/Portals/PortalModel/PortalAdminData";

interface PortalFieldsAuthProps {
  PrimaryRoleID?: number
}

interface PortalFieldsProps {
  isValid: (data: PortalAdminData, valid: boolean) => void
  onNameChange: (name: string) => void
}

interface PortalFieldsValidity {
  nameValid: boolean
  emailValid: boolean
  phoneValid: boolean
  roleValid: boolean
  firstNameValid: boolean
  lastNameValid: boolean
}

export interface PortalFieldsData {
  name: string
  email: string
  phone: string
  roleID?: number
  firstName: string,
  lastName: string,
}

interface PortalFieldsModalState extends PortalFieldsData, PortalFieldsValidity {
  nameChecking: boolean
  nameAvailable?: boolean
  emailChecking: boolean
  emailAvailable?: boolean
  rolesLoading: boolean
  roles?: Role[]
}

export class PortalFields extends React.PureComponent<PortalFieldsProps & PortalFieldsAuthProps, PortalFieldsModalState> {
  emptyData: PortalFieldsData = {
    name: "",
    email: "",
    phone: "",
    roleID: undefined,
    firstName: "",
    lastName: "",
  }
  positiveValidity: PortalFieldsValidity = {
    nameValid: true,
    emailValid: true,
    phoneValid: true,
    roleValid: true,
    firstNameValid: true,
    lastNameValid: true
  }
  defaultState: PortalFieldsModalState = {
    ...this.emptyData,
    ...this.positiveValidity,
    nameChecking: false,
    nameAvailable: undefined,
    emailChecking: false,
    emailAvailable: undefined,
    rolesLoading: false,
  }
  state: PortalFieldsModalState = { ...this.defaultState }

  checkName$ = new Subject<string>()
  checkEmail$ = new Subject<string>()

  nameExistsSubscription?: Subscription
  emailExistsSubscription?: Subscription
  userRolesSubscription?: Subscription

  componentDidMount() {
    this.nameExistsSubscription = this.checkName$.pipe(
      map(name => name.trim()),
      filter(name => this.isNameValid(name)),
      switchMap(name => concat(
        of({ loading: true }),
        CompanyNameCheck(name).pipe(
          map(result => ({ loading: false, result: result }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: Addresses | null }) => this.setState({
          nameChecking: o.loading,
          nameAvailable: !o.result
        }, () => this.isValid()),
      (error) => this.handleAPIError(error, { nameChecking: false, nameAvailable: undefined })
    )

    this.emailExistsSubscription = this.checkEmail$.pipe(
      map(name => name.trim()),
      filter(name => this.isEmailValid(name)),
      switchMap(name => concat(
        of({ loading: true }),
        EmailCheck(name).pipe(
          map(result => ({ loading: false, result: result.IsEmailExists }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: boolean }) => this.setState({
        emailChecking: o.loading,
        emailAvailable: !o.result
      }, () => this.isValid()),
      (error) => this.handleAPIError(error, { emailChecking: false, emailAvailable: undefined })
    )

    const roleID = this.props.PrimaryRoleID || 0
    this.setState({ rolesLoading: true })
    this.userRolesSubscription = GetRoles(roleID).subscribe(
      (data: Role[]) => this.setState({
        roles: data,
        roleID: data.length === 1 ? data[0].RoleID : undefined,
        rolesLoading: false
      }),
      (error) => this.handleAPIError(error, { rolesLoading: false, emailAvailable: undefined })
    )
  }

  componentWillUnmount() {
    this.nameExistsSubscription && this.nameExistsSubscription.unsubscribe()
    this.emailExistsSubscription && this.emailExistsSubscription.unsubscribe()
    this.userRolesSubscription && this.userRolesSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  isNameValid = (value: string) => value.trim().length > 4
  isEmailValid = (value: string) => EmailRegexp.test(value)
  isPhoneValid = (value: string) => PhoneRegexp.test(value)
  isRoleValid = (value: number | undefined): boolean => !!value && !!this.state.roles?.find(item => item.RoleID === value)
  isFirstNameValid = (value: string) => !!value.trim()
  isLastNameValid = (value: string) => !!value.trim()

  isFieldsValid = () => {
    const {
      name, nameValid, nameAvailable,
      email, emailValid, emailAvailable,
      phone, phoneValid,
      roleID, roleValid,
      firstName, firstNameValid,
      lastName, lastNameValid,
    } = this.state
    return !!name && nameValid && !!nameAvailable && !!email && emailValid && !!emailAvailable && !!phone && phoneValid
        && !!roleID && roleValid && !!firstName && firstNameValid && !!lastName && lastNameValid
  }

  isValid = () => {
    const data: PortalAdminData = {
      Name: this.state.name,
      Email: this.state.email,
      Phone: this.state.phone,
      RoleID: this.state.roleID || 0,
      FirstName: this.state.firstName,
      LastName: this.state.lastName,
    }
    this.props.isValid(data, this.isFieldsValid())
  }
  handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({
      name: value,
      nameValid: this.isNameValid(value),
      nameAvailable: undefined
    }, () => this.props.onNameChange(value))
  }

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ email: value, emailValid: this.isEmailValid(value), emailAvailable: undefined })
  }

  handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState(
      { phone: value, phoneValid: this.isPhoneValid(value) },
      () => this.isValid()
    )
  }

  handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value)
    this.setState(
      { roleID: value, roleValid: this.isRoleValid(value) },
      () => this.isValid()
    )
  }

  handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState(
      { firstName: value, firstNameValid: this.isFirstNameValid(value) },
      () => this.isValid()
    )
  }

  handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState(
      { lastName: value, lastNameValid: this.isLastNameValid(value) },
      () => this.isValid()
    )
  }

  render() {
    const {
      name, nameValid, nameChecking, nameAvailable,
      email, emailValid, emailChecking, emailAvailable,
      phone, phoneValid,
      roleID, roleValid, rolesLoading,
      firstName, firstNameValid,
      lastName, lastNameValid
    } = this.state

    return (
      <>
        <Form.Row className="form-group required">
          <Form.Label column sm={4} htmlFor="name">Portal Name</Form.Label>
          <Col sm={8}>
            <Form.Control id="name"
              type="text"
              value={name}
              onChange={this.handleNameChange}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => this.checkName$.next(e.target.value)}
              isInvalid={!nameValid}
              disabled={nameChecking} />
            {!nameChecking && !nameValid &&
              <Form.Control.Feedback type="invalid" className="d-block">Please enter minimum 5 characters</Form.Control.Feedback>}
            {!nameChecking && nameAvailable &&
              <Form.Control.Feedback type="valid" className="d-block">Portal name is available</Form.Control.Feedback>}
            {!nameChecking && nameAvailable === false &&
              <Form.Control.Feedback type="invalid" className="d-block">Portal name is already in use</Form.Control.Feedback>}
          </Col>
        </Form.Row>
        <Form.Row className="form-group required">
          <Form.Label column sm={4} htmlFor="email">Portal Admin Email Address</Form.Label>
          <Col sm={8}>
            <Form.Control id="email"
              type="text"
              value={email}
              onChange={this.handleEmailChange}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => this.checkEmail$.next(e.target.value)}
              isInvalid={!emailValid}
              disabled={emailChecking} />
            {!emailChecking && !emailValid &&
              <Form.Control.Feedback type="invalid" className="d-block">Please enter valid email</Form.Control.Feedback>}
            {!emailChecking && emailAvailable &&
              <Form.Control.Feedback type="valid" className="d-block">Email is available</Form.Control.Feedback>}
            {!emailChecking && emailAvailable === false &&
              <Form.Control.Feedback type="invalid" className="d-block">Email is already in use</Form.Control.Feedback>}
          </Col>
        </Form.Row>
        {emailAvailable !== false && <Form.Row className="form-group required">
          <Form.Label column sm={4} htmlFor="phone">Portal Admin Personal Phone</Form.Label>
          <Col sm={8}>
            <Form.Control id="phone" type="text" as={MaskedInput} mask="111-111-1111" value={phone}
              onChange={this.handlePhoneChange} isInvalid={!phoneValid} />
            <Form.Text>
              This phone number will be used to validate the Administrator. Please provide a direct number or
              mobile number (not a main company number or extension)
            </Form.Text>
          </Col>
        </Form.Row>}
        <Form.Row className="form-group required">
          <Form.Label column sm={4} htmlFor="role">Portal Admin First Name</Form.Label>
          <Col sm={8}>
            <Form.Control id="firstName"
                          type="text"
                          value={firstName}
                          onChange={this.handleFirstNameChange}
                          onBlur={this.handleFirstNameChange}
                          isInvalid={!firstNameValid}/>
          </Col>
        </Form.Row>
        <Form.Row className="form-group required">
          <Form.Label column sm={4} htmlFor="role">Portal Admin Last Name</Form.Label>
          <Col sm={8}>
            <Form.Control id="lastName"
                          type="text"
                          value={lastName}
                          onChange={this.handleLastNameChange}
                          onBlur={this.handleLastNameChange}
                          isInvalid={!lastNameValid}/>
          </Col>
        </Form.Row>
        <Form.Row className="form-group required">
          <Form.Label column sm={4} htmlFor="role">User's Role</Form.Label>
          <Col sm={8}>
            <Form.Control id="role" as="select" onChange={this.handleRoleChange} value={roleID || ""} isInvalid={!roleValid} disabled={rolesLoading}>
              {rolesLoading && <option value="">Loading ...</option> }
              {!rolesLoading && this.state.roles && this.state.roles.length > 1 && <option value="">Select Role</option>}
              {!rolesLoading && this.state.roles?.map(role => <option value={role.RoleID} key={role.RoleID}>{role.Title}</option>)}
            </Form.Control>
          </Col>
        </Form.Row>
      </>
    )
  }
}

function mapStateToProps(state: AppState): PortalFieldsAuthProps {
  return {
    PrimaryRoleID: RoleIdForSelectedCompany(state)
  }
}

const ConnectedPortalFields = connect(mapStateToProps, null)(PortalFields)
export default ConnectedPortalFields
